// AI Recommendations subview — wraps the backend /api/ai/expense-audit endpoint.
import React, { useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import AiMarkdown from '../../components/AiMarkdown';

export function AiRecommendationsView({ ai, setAi, hasData }) {
  const [generating, setGenerating] = useState(false);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/ai/expense-audit');
      setAi(r.data);
      toast.success('Recommendations regenerated.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not generate recommendations.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-white border-t-4 border-indie-evergreen p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="label-editorial">AI Recommendations · Claude Sonnet 4.5</div>
            <div className="text-xl font-bold text-indie-navy mt-1">What should we do with our recurring spend?</div>
            <p className="text-sm text-indie-ink/70 mt-1">
              Reads every vendor row, joined QBO actuals, scores, and renewals → produces a ranked, plain-English action list.
              Designed to regenerate each quarter automatically.
            </p>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generating || !hasData}
            data-testid="ea-ai-generate"
            className="bg-indie-evergreen text-white font-semibold uppercase tracking-wider text-xs px-4 py-2.5 hover:bg-indie-evergreen/85 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <RefreshCw size={13} className={generating ? 'animate-spin' : ''} />
            {generating ? 'Generating…' : (ai ? 'Refresh recommendations' : 'Generate recommendations')}
          </button>
        </div>
        {!hasData && <p className="text-xs text-indie-ink/55 mt-3">Sync the Vendor Master and Expense Detail sheets first — the audit AI needs data.</p>}
      </div>

      {ai?.narrative ? (
        <div data-testid="ea-ai-narrative" className="bg-white border-t-4 border-indie-cobalt p-6 ai-narrative">
          <div className="flex items-center justify-between text-xs text-indie-ink/55 mb-3">
            <span>Generated {new Date(ai.created_at).toLocaleString()}</span>
            <span>By {ai.created_by}</span>
          </div>
          <AiMarkdown>{ai.narrative}</AiMarkdown>
        </div>
      ) : (
        <div className="bg-indie-cream border-l-4 border-indie-yellow p-5 text-sm text-indie-ink/75">
          No recommendations yet. Click "Generate recommendations" once you have at least one vendor synced — Claude will write the quarterly summary in Mike-Michalowicz-adjacent voice, ranked by dollar impact.
        </div>
      )}
    </div>
  );
}
